import { createFeature, createReducer, on } from '@ngrx/store'
import {
    addChildToSelectionRoot,
    findParentSelectionNodeInRoot,
    findSelectionNodeInRoot,
    FreeSelection,
    getAllOwnPointsInNode,
    getAllOwnAndChildrenPointsInNode,
    HierarchicalSelection,
    HierarchicalSelectionNode,
    HierarchicalSelectionRoot,
    removeChildrenFromSelectionRoot,
    removeChildrenFromSelectionRootRecursively,
    SelectionBase,
    SelectionMode,
    SelectionState,
    SelectionType,
    UnsupervisedSelectionNode,
    UnsupervisedSelectionRoot,
    UnsupervisedSelection,
} from './selection.state'
import { selectionActions } from './selection.actions'
import { getAnnotationColor } from '../../shared/utils/shaders'
import { caseActions } from '../case/case.actions'
import {
    HierarchicalSelectionNodeQueryParam,
    HierarchicalSelectionRootQueryParam,
} from '../../shared/model/query-params-types'

const createNewSelectionBase = (): SelectionBase => ({
    name: undefined,
    modified: false,
    points: new Set<number>(),
    segSource: undefined,
    annotationVisible: true,
    annotationColor: getAnnotationColor().hexString,
    annotationOpacity: 1,
})

const createNewFreeSelection = (): FreeSelection => ({
    ...createNewSelectionBase(),
    type: SelectionType.Free,
})

const createNewHierarchicalSelectionRoot = (name: string | undefined): HierarchicalSelection => ({
    name: name,
    modified: false,
    type: SelectionType.Hierarchical,
    children: [],
    unsavedLevel: createNewSelectionBase(),
    selectedHierarchyName: undefined,
    segSource: undefined,
})

const createNewUnsupervisedSelectionRoot = (name: string | undefined): UnsupervisedSelection => ({
    name: name,
    modified: false,
    type: SelectionType.Unsupervised,
    children: [],
    unsavedLevel: createNewSelectionBase(),
    selectedUnsupervisedName: undefined,
    segSource: undefined,
})

const createInitialState = (): SelectionState => ({
    type: SelectionType.Free,
    mode: SelectionMode.Add,
    freeSelection: createNewFreeSelection(),
    savedFreeSelections: new Map<string, FreeSelection>(),
    originalSavedFreeSelections: new Map<string, FreeSelection>(),
    hierarchicalSelection: createNewHierarchicalSelectionRoot(undefined),
    savedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>(),
    originalSavedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>(),
    displayedOnScatterPlot: [],
    unsupervisedSelection: createNewUnsupervisedSelectionRoot(undefined),
    savedUnsupervisedSelections: new Map<string, UnsupervisedSelectionRoot>(),
    resolution: 6,
})

export const selectionFeature = createFeature({
    name: 'selection',
    reducer: createReducer<SelectionState>(
        createInitialState(),
        on(selectionActions.setType, (state, { selectionType }): SelectionState => {
            return {
                ...state,
                type: selectionType,
                freeSelection: createNewFreeSelection(),
                hierarchicalSelection: createNewHierarchicalSelectionRoot(undefined),
            }
        }),
        on(selectionActions.setMode, (state, { mode }): SelectionState => {
            return {
                ...state,
                mode,
            }
        }),
        on(selectionActions.setResolution, (state, { resolution }): SelectionState => {
            return {
                ...state,
                resolution,
            }
        }),
        on(selectionActions.updateSelection, (state, { points }): SelectionState => {
            if (state.type == SelectionType.Hierarchical) {
                const newPoints = new Set(state.hierarchicalSelection.unsavedLevel.points)

                if (state.mode == SelectionMode.Add) {
                    points.forEach(p => newPoints.add(p))
                }
                if (state.mode == SelectionMode.Subtract) {
                    points.forEach(p => newPoints.delete(p))
                }
                const selectionRoot = {
                    ...state.hierarchicalSelection,
                    unsavedLevel: {
                        ...state.hierarchicalSelection.unsavedLevel,
                        points: newPoints,
                    },
                }
                updateSavedHierarchicalSelection(selectionRoot, state)
                return {
                    ...state,
                    hierarchicalSelection: selectionRoot,
                }
            }
            if (state.type == SelectionType.Unsupervised) {
                const newPoints = new Set(state.hierarchicalSelection.unsavedLevel.points)

                if (state.mode == SelectionMode.Add) {
                    points.forEach(p => newPoints.add(p))
                }
                if (state.mode == SelectionMode.Subtract) {
                    points.forEach(p => newPoints.delete(p))
                }
                const selectionRoot = {
                    ...state.hierarchicalSelection,
                    unsavedLevel: {
                        ...state.hierarchicalSelection.unsavedLevel,
                        points: newPoints,
                    },
                }
                updateSavedHierarchicalSelection(selectionRoot, state)
                return {
                    ...state,
                    hierarchicalSelection: selectionRoot,
                }
            }
            const newPoints = new Set(state.freeSelection.points)
            let modified = false

            if (state.mode == SelectionMode.Add) {
                points.forEach(p => newPoints.add(p))
            }
            if (state.mode == SelectionMode.Subtract) {
                points.forEach(p => newPoints.delete(p))
            }

            if (state.freeSelection.name != undefined) {
                const previousSelection =
                    state.savedFreeSelections.get(state.freeSelection.name)?.points ?? new Set()
                if (
                    !(
                        previousSelection.size === newPoints.size &&
                        [...previousSelection].every(value => newPoints.has(value))
                    )
                ) {
                    modified = true
                }
            } else if (newPoints.size > 0) {
                modified = true
            }

            return {
                ...state,
                freeSelection: {
                    ...state.freeSelection,
                    points: newPoints,
                    modified: modified,
                },
            }
        }),

        on(selectionActions.clearSelection, (state): SelectionState => {
            if (state.type == SelectionType.Hierarchical) {
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...state.hierarchicalSelection,
                        unsavedLevel: {
                            ...state.hierarchicalSelection.unsavedLevel,
                            points: new Set<number>(),
                        },
                    },
                }
            }
            if (state.type == SelectionType.Unsupervised) {
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...state.hierarchicalSelection,
                        unsavedLevel: {
                            ...state.hierarchicalSelection.unsavedLevel,
                            points: new Set<number>(),
                        },
                    },
                }
            }
            let modified = false
            if (state.freeSelection.name != undefined) {
                const previousSelection =
                    state.savedFreeSelections.get(state.freeSelection.name)?.points ?? new Set()
                if (previousSelection.size !== 0) {
                    modified = true
                }
            }
            return {
                ...state,
                freeSelection: {
                    ...state.freeSelection,
                    points: new Set<number>(),
                    modified: modified,
                },
            }
        }),
        on(
            selectionActions.loadingFreeSelectionsSucceeded,
            (state, { selections, queryParams }): SelectionState => {
                const savedFreeSelections = new Map(
                    Array.from(selections.entries()).map(
                        ([key, selection]: [string, FreeSelection]) => {
                            const queryParamsSelection = queryParams.get(key)
                            return [
                                key,
                                {
                                    ...selection,
                                    annotationVisible: queryParamsSelection
                                        ? queryParamsSelection.annotationVisible
                                        : selection.annotationVisible,
                                    annotationOpacity: queryParamsSelection
                                        ? queryParamsSelection.annotationOpacity
                                        : selection.annotationOpacity,
                                    annotationColor: queryParamsSelection
                                        ? queryParamsSelection.annotationColor
                                        : selection.annotationColor,
                                },
                            ]
                        }
                    )
                )
                return {
                    ...state,
                    savedFreeSelections: savedFreeSelections,
                    originalSavedFreeSelections: savedFreeSelections,
                }
            }
        ),
        on(
            selectionActions.loadingHierarchicalSelectionsSucceeded,
            (state, { selections, queryParams }): SelectionState => {
                const loadedSelections = selections
                loadedSelections.forEach((selectionRoot, key) => {
                    const queryRoot = queryParams.get(key)
                    if (queryRoot) {
                        updateSelectionRootWithQueryParams(selectionRoot, queryRoot)
                    }
                })
                return {
                    ...state,
                    savedHierarchicalSelections: loadedSelections,
                    originalSavedHierarchicalSelections: loadedSelections,
                }
            }
        ),
        on(
            selectionActions.loadingUnsupervisedSelectionsSucceeded,
            (state, { selections, queryParams }): SelectionState => {
                const loadedSelections = selections
                loadedSelections.forEach((selectionRoot, key) => {
                    const queryRoot = queryParams.get(key)
                    if (queryRoot) {
                        updateSelectionRootWithQueryParams(selectionRoot, queryRoot)
                    }
                })
                return {
                    ...state,
                    savedUnsupervisedSelections: loadedSelections,
                }
            }
        ),
        on(selectionActions.setFreeSelection, (state, { name }): SelectionState => {
            if (!name) {
                return {
                    ...state,
                    freeSelection: createNewFreeSelection(),
                }
            }
            return {
                ...state,
                freeSelection: state.savedFreeSelections.get(name) ?? state.freeSelection,
            }
        }),
        on(selectionActions.setFreeSelectionSegSource, (state, { segSource }): SelectionState => {
            // Only update if the value is different
            if (state.freeSelection.segSource !== segSource) {
                return {
                    ...state,
                    freeSelection: {
                        ...state.freeSelection,
                        segSource: segSource,
                    },
                }
            }
            return state
        }),
        on(
            selectionActions.saveNewHierarchicalSelectionRoot,
            (state, { name, segSource }): SelectionState => {
                const newHierarchicalSelectionRoot = createNewHierarchicalSelectionRoot(name)
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...newHierarchicalSelectionRoot,
                        segSource: segSource,
                    },
                }
            }
        ),
        on(selectionActions.setHierarchicalSelectionRoot, (state, { name }): SelectionState => {
            return {
                ...state,
                hierarchicalSelection: {
                    ...state.savedHierarchicalSelections.get(name),
                    selectedHierarchyName: undefined,
                    unsavedLevel: createNewSelectionBase(),
                } as HierarchicalSelection,
            }
        }),
        on(selectionActions.setHierarchicalSelectionNode, (state, { name }): SelectionState => {
            return {
                ...state,
                hierarchicalSelection: {
                    ...state.hierarchicalSelection,
                    unsavedLevel: createNewSelectionBase(),
                    selectedHierarchyName: name,
                },
            }
        }),
        on(
            selectionActions.saveNewAutoCellTypeDetectionRoot,
            (state, { name, segSource }): SelectionState => {
                const newUnsupervisedSelectionRoot = createNewUnsupervisedSelectionRoot(name)
                return {
                    ...state,
                    unsupervisedSelection: {
                        ...newUnsupervisedSelectionRoot,
                        segSource: segSource,
                    },
                }
            }
        ),
        on(selectionActions.setAutoCellTypeDetectionRoot, (state, { name }): SelectionState => {
            return {
                ...state,
                unsupervisedSelection: {
                    ...state.savedUnsupervisedSelections.get(name),
                    selectedUnsupervisedName: undefined,
                    unsavedLevel: createNewSelectionBase(),
                } as any,
            }
        }),
        on(selectionActions.setAutoCellTypeDetectionNode, (state, { name }): SelectionState => {
            return {
                ...state,
                hierarchicalSelection: {
                    ...state.hierarchicalSelection,
                    unsavedLevel: createNewSelectionBase(),
                    selectedHierarchyName: name,
                },
            }
        }),
        on(
            selectionActions.setVisibleSelectionsOnScatterPlot,
            (state, { selectionNames }): SelectionState => {
                let displayedOnScatterPlot: any

                if (state.type == SelectionType.Free) {
                    displayedOnScatterPlot = [...state.savedFreeSelections.values()].filter(
                        selection => selectionNames.includes(selection.name!)
                    )
                }
                if (state.type == SelectionType.Hierarchical) {
                    const flattenedSelections = flattenHierarchicalSelection(
                        state.hierarchicalSelection
                    )
                    displayedOnScatterPlot = flattenedSelections.filter(selection =>
                        selectionNames.includes(selection.name!)
                    )
                }
                if (state.type == SelectionType.Unsupervised) {
                    const flattenedSelections = flattenHierarchicalSelection(
                        state.hierarchicalSelection
                    )
                    displayedOnScatterPlot = flattenedSelections.filter(selection =>
                        selectionNames.includes(selection.name!)
                    )
                }

                return {
                    ...state,
                    displayedOnScatterPlot: displayedOnScatterPlot,
                }
            }
        ),
        on(selectionActions.saveNewFreeSelection, (state, { name }): SelectionState => {
            return {
                ...state,
                freeSelection: {
                    ...state.freeSelection,
                    name,
                },
            }
        }),
        on(selectionActions.saveNewHierarchicalSelectionNode, (state, { name }): SelectionState => {
            const selectionRoot = {
                ...copySelectionRoot(state.hierarchicalSelection),
                unsavedLevel: state.hierarchicalSelection.unsavedLevel,
                selectedHierarchyName: name,
            }

            selectionRoot.selectedHierarchyName = name

            const selectionName = state.hierarchicalSelection.selectedHierarchyName

            const parentSelection = findSelectionNodeInRoot(selectionRoot, selectionName)
            if (parentSelection) {
                state.hierarchicalSelection.unsavedLevel.points.forEach((point: number) =>
                    parentSelection.points.delete(point)
                )
            }

            const selectionToSave: HierarchicalSelectionNode = {
                ...selectionRoot.unsavedLevel,
                name: name,
                children: undefined,
                segSource: selectionRoot.segSource,
            }

            addChildToSelectionRoot(selectionRoot, selectionToSave, selectionName)
            return {
                ...state,
                hierarchicalSelection: selectionRoot,
            }
        }),
        on(selectionActions.deleteHierarchicalSelectionNode, (state): SelectionState => {
            const selectionRoot = copySelectionRoot(state.hierarchicalSelection)
            const selectionName = state.hierarchicalSelection.selectedHierarchyName
            if (!selectionName) return state
            const selectionToDelete = findSelectionNodeInRoot(selectionRoot, selectionName)
            const parentSelection = findParentSelectionNodeInRoot(selectionRoot, selectionName)

            if (selectionToDelete && parentSelection) {
                const pointsToReassign = getAllOwnPointsInNode(selectionToDelete)
                pointsToReassign.forEach((point: number) => {
                    parentSelection.points.add(point)
                })
                removeChildrenFromSelectionRoot(selectionRoot, selectionName)
            } else if (selectionToDelete) {
                removeChildrenFromSelectionRoot(selectionRoot, selectionName)
            }
            return {
                ...state,
                hierarchicalSelection: {
                    ...selectionRoot,
                    unsavedLevel: createNewSelectionBase(),
                    selectedHierarchyName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(selectionActions.deleteHierarchicalSelectionNodeRecursively, (state): SelectionState => {
            const selectionRoot = copySelectionRoot(state.hierarchicalSelection)
            const selectionName = state.hierarchicalSelection.selectedHierarchyName
            if (!selectionName) return state
            const selectionToDelete = findSelectionNodeInRoot(selectionRoot, selectionName)
            const parentSelection = findParentSelectionNodeInRoot(selectionRoot, selectionName)

            if (selectionToDelete && parentSelection) {
                const pointsToReassign = getAllOwnAndChildrenPointsInNode(selectionToDelete)
                pointsToReassign.forEach((point: number) => {
                    parentSelection.points.add(point)
                })
                removeChildrenFromSelectionRootRecursively(selectionRoot, selectionName)
            } else if (selectionToDelete) {
                removeChildrenFromSelectionRootRecursively(selectionRoot, selectionName)
            }
            return {
                ...state,
                hierarchicalSelection: {
                    ...selectionRoot,
                    unsavedLevel: createNewSelectionBase(),
                    selectedHierarchyName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(selectionActions.saveNewAutoCellTypeDetectionNode, (state, { name }): SelectionState => {
            const selectionRoot = {
                ...copySelectionRoot(state.unsupervisedSelection),
                unsavedLevel: state.unsupervisedSelection.unsavedLevel,
                selectedUnsupervisedName: name,
            }

            selectionRoot.selectedUnsupervisedName = name

            const selectionName = state.unsupervisedSelection.selectedUnsupervisedName

            const parentSelection = findSelectionNodeInRoot(selectionRoot, selectionName)
            if (parentSelection) {
                state.unsupervisedSelection.unsavedLevel.points.forEach((point: number) =>
                    parentSelection.points.delete(point)
                )
            }

            const selectionToSave: UnsupervisedSelectionNode = {
                ...selectionRoot.unsavedLevel,
                name: name,
                children: undefined,
                segSource: selectionRoot.segSource,
            }

            addChildToSelectionRoot(selectionRoot, selectionToSave, selectionName)
            return {
                ...state,
                unsupervisedSelection: selectionRoot,
            }
        }),
        on(
            selectionActions.savingFreeSelectionSucceeded,
            (state, { selection }): SelectionState => {
                return {
                    ...state,
                    savedFreeSelections: new Map<string, FreeSelection>([
                        ...state.savedFreeSelections,
                        [selection.name!, { ...selection, modified: false }],
                    ]),
                    freeSelection: { ...state.freeSelection, modified: false },
                }
            }
        ),
        on(
            selectionActions.savingHierarchicalSelectionRootSucceeded,
            (state, { selection }): SelectionState => {
                const savedHierarchicalSelections = new Map<string, HierarchicalSelectionRoot>([
                    ...state.savedHierarchicalSelections,
                ])
                savedHierarchicalSelections.set(selection.name!, selection)
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...state.hierarchicalSelection,
                        unsavedLevel: createNewSelectionBase(),
                    },
                    savedHierarchicalSelections: savedHierarchicalSelections,
                }
            }
        ),
        on(selectionActions.savingHierarchicalSelectionRootFailed, (state): SelectionState => {
            const parentSelection = findParentSelectionNodeInRoot(
                state.hierarchicalSelection,
                state.hierarchicalSelection.unsavedLevel.name
            )
            return {
                ...state,
                hierarchicalSelection: {
                    ...state.hierarchicalSelection,
                    unsavedLevel: createNewSelectionBase(),
                    selectedHierarchyName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(
            selectionActions.savingAutoCellTypeDetectionRootSucceeded,
            (state, { selection }): SelectionState => {
                const savedUnsupervisedSelections = new Map<string, UnsupervisedSelectionRoot>([
                    ...state.savedUnsupervisedSelections,
                ])
                savedUnsupervisedSelections.set(selection.name!, selection)
                return {
                    ...state,
                    unsupervisedSelection: {
                        ...state.unsupervisedSelection,
                        unsavedLevel: createNewSelectionBase(),
                    },
                    savedUnsupervisedSelections: savedUnsupervisedSelections,
                }
            }
        ),
        on(selectionActions.savingAutoCellTypeDetectionRootFailed, (state): SelectionState => {
            const parentSelection = findParentSelectionNodeInRoot(
                state.unsupervisedSelection,
                state.unsupervisedSelection.unsavedLevel.name
            )
            return {
                ...state,
                unsupervisedSelection: {
                    ...state.unsupervisedSelection,
                    unsavedLevel: createNewSelectionBase(),
                    selectedUnsupervisedName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(selectionActions.deletingFreeSelectionSucceeded, (state): SelectionState => {
            const savedSelections = new Map<string, FreeSelection>([...state.savedFreeSelections])
            savedSelections.delete(state.freeSelection.name!)
            return {
                ...state,
                savedFreeSelections: savedSelections,
                freeSelection: createNewFreeSelection(),
            }
        }),
        on(selectionActions.deletingHierarchicalSelectionRootSucceeded, (state): SelectionState => {
            const savedHierarchicalSelections = new Map<string, HierarchicalSelectionRoot>([
                ...state.savedHierarchicalSelections,
            ])
            savedHierarchicalSelections.delete(state.hierarchicalSelection.name!)
            return {
                ...state,
                savedHierarchicalSelections: savedHierarchicalSelections,
                hierarchicalSelection: createNewHierarchicalSelectionRoot(undefined),
            }
        }),
        on(selectionActions.deletingAutoCellTypeDetectionRootSucceeded, (state): SelectionState => {
            const savedUnsupervisedSelections = new Map<string, UnsupervisedSelectionRoot>([
                ...state.savedUnsupervisedSelections,
            ])
            savedUnsupervisedSelections.delete(state.unsupervisedSelection.name!)
            return {
                ...state,
                savedUnsupervisedSelections: savedUnsupervisedSelections,
                unsupervisedSelection: createNewUnsupervisedSelectionRoot(undefined),
            }
        }),
        on(selectionActions.setSelectionVisibility, (state, action): SelectionState => {
            if (action.selectionType === SelectionType.Hierarchical) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionVisible(action.visible, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationVisible = action.visible
                return updateSavedHierarchicalSelection(selectionRoot, state)
            }

            if (action.selectionType === SelectionType.Unsupervised) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionVisible(action.visible, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationVisible = action.visible
                return updateSavedHierarchicalSelection(selectionRoot, state)
            }

            if (!action.selectionName) {
                return updateFreeSelectionVisible(action.visible, state)
            }

            const selection = state.savedFreeSelections.get(action.selectionName!)
            if (!selection || !selection.name) {
                return state
            }

            if (action.selectionName === state.freeSelection.name) {
                return updateCurrentAndSavedFreeSelectionVisible(action.visible, selection, state)
            }

            return updateSavedFreeSelectionVisible(action.visible, selection, state)
        }),
        on(selectionActions.setSelectionOpacity, (state, action): SelectionState => {
            if (action.selectionType === SelectionType.Hierarchical) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionOpacity(action.opacity, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationOpacity = action.opacity
                return updateSavedHierarchicalSelection(selectionRoot, state)
            }

            if (action.selectionType === SelectionType.Unsupervised) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionOpacity(action.opacity, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationOpacity = action.opacity
                return updateSavedHierarchicalSelection(selectionRoot, state)
            }
            if (!action.selectionName) {
                return updateFreeSelectionOpacity(action.opacity, state)
            }

            const selection = state.savedFreeSelections.get(action.selectionName!)
            if (!selection || !selection.name) {
                return state
            }

            if (action.selectionName === state.freeSelection.name) {
                return updateCurrentAndSavedFreeSelectionOpacity(action.opacity, selection, state)
            }

            return updateSavedFreeSelectionOpacity(action.opacity, selection, state)
        }),
        on(selectionActions.setSelectionShaderColor, (state, action): SelectionState => {
            if (action.selectionType === SelectionType.Hierarchical) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionColor(action.color, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationColor = action.color
                return updateSavedHierarchicalSelection(selectionRoot, state)
            }

            if (action.selectionType === SelectionType.Unsupervised) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionColor(action.color, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationColor = action.color
                return updateSavedHierarchicalSelection(selectionRoot, state)
            }
            // if name is undefined, update current (unsaved) selection
            if (!action.selectionName) {
                return updateFreeSelectionColor(action.color, state)
            }

            const selection = state.savedFreeSelections.get(action.selectionName!)
            if (!selection || !selection.name) {
                return state
            }

            if (action.selectionName === state.freeSelection.name) {
                return updateCurrentAndSavedFreeSelectionColor(action.color, selection, state)
            }

            return updateSavedFreeSelectionColor(action.color, selection, state)
        }),
        on(caseActions.loadCase, (): SelectionState => createInitialState())
    ),
})

const updateFreeSelectionVisible = (visible: boolean, state: SelectionState): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationVisible: visible,
    },
})

const updateHierarchicalSelectionVisible = (
    visible: boolean,
    state: SelectionState
): SelectionState => ({
    ...state,
    hierarchicalSelection: {
        ...state.hierarchicalSelection,
        unsavedLevel: {
            ...state.hierarchicalSelection.unsavedLevel,
            annotationVisible: visible,
        },
    },
})

const updateCurrentAndSavedFreeSelectionVisible = (
    visible: boolean,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationVisible: visible,
    },
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationVisible: visible }],
    ]),
})

const updateSavedFreeSelectionVisible = (
    visible: boolean,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationVisible: visible }],
    ]),
})

const updateFreeSelectionOpacity = (opacity: number, state: SelectionState): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationOpacity: opacity,
    },
})

const updateHierarchicalSelectionOpacity = (
    opacity: number,
    state: SelectionState
): SelectionState => ({
    ...state,
    hierarchicalSelection: {
        ...state.hierarchicalSelection,
        unsavedLevel: {
            ...state.hierarchicalSelection.unsavedLevel,
            annotationOpacity: opacity,
        },
    },
})

const updateCurrentAndSavedFreeSelectionOpacity = (
    opacity: number,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationOpacity: opacity,
    },
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationOpacity: opacity }],
    ]),
})

const updateSavedFreeSelectionOpacity = (
    opacity: number,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationOpacity: opacity }],
    ]),
})

const updateFreeSelectionColor = (color: string, state: SelectionState): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationColor: color,
    },
})

const updateHierarchicalSelectionColor = (
    color: string,
    state: SelectionState
): SelectionState => ({
    ...state,
    hierarchicalSelection: {
        ...state.hierarchicalSelection,
        unsavedLevel: {
            ...state.hierarchicalSelection.unsavedLevel,
            annotationColor: color,
        },
    },
})

const updateCurrentAndSavedFreeSelectionColor = (
    color: string,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationColor: color,
    },
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationColor: color }],
    ]),
})

const updateSavedFreeSelectionColor = (
    color: string,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationColor: color }],
    ]),
})

const updateSavedHierarchicalSelection = (
    selectionRoot: HierarchicalSelectionRoot,
    state: SelectionState
): SelectionState => ({
    ...state,
    hierarchicalSelection: { ...state.hierarchicalSelection, children: selectionRoot.children },
    savedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>([
        ...state.savedHierarchicalSelections,
        [selectionRoot.name!, selectionRoot],
    ]),
})

const copySelectionRoot = (selection: HierarchicalSelectionRoot): HierarchicalSelectionRoot => {
    if (!selection.children) return { ...selection }
    return {
        ...selection,
        children: selection.children?.map(child => copySelectionNode(child)),
    }
}

const copySelectionNode = (selection: HierarchicalSelectionNode): HierarchicalSelectionNode => {
    if (!selection.children) return { ...selection }
    return {
        ...selection,
        points: new Set(selection.points),
        children: selection.children?.map(child => copySelectionNode(child)),
    }
}

const updateSelectionRootWithQueryParams = (
    selectionRoot: HierarchicalSelectionRoot,
    queryParams: HierarchicalSelectionRootQueryParam
): void => {
    if (!selectionRoot.children) return
    selectionRoot.children.forEach(child => {
        updateSelectionNodeWithQueryParams(child, queryParams)
    })
}

const updateSelectionNodeWithQueryParams = (
    selectionNode: HierarchicalSelectionNode,
    queryParams: HierarchicalSelectionRootQueryParam
): void => {
    const queryParamsNode = findSelectionNodeInRootQueryParam(queryParams, selectionNode.name)

    selectionNode.annotationVisible = queryParamsNode
        ? queryParamsNode.annotationVisible
        : selectionNode.annotationVisible
    selectionNode.annotationOpacity = queryParamsNode
        ? queryParamsNode.annotationOpacity
        : selectionNode.annotationOpacity
    selectionNode.annotationColor = queryParamsNode
        ? queryParamsNode.annotationColor
        : selectionNode.annotationColor

    if (!selectionNode.children) return
    selectionNode.children.forEach(child => {
        updateSelectionNodeWithQueryParams(child, queryParams)
    })
}

const findSelectionNodeInRootQueryParam = (
    selection: HierarchicalSelectionRootQueryParam,
    name: string | undefined
): HierarchicalSelectionNodeQueryParam | null => {
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNodeQueryParam(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

const findSelectionNodeInNodeQueryParam = (
    selection: HierarchicalSelectionNodeQueryParam,
    name: string | undefined
): HierarchicalSelectionNodeQueryParam | null => {
    if (selection.name === name) {
        return selection
    }
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNodeQueryParam(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

const flattenHierarchicalSelection = (
    root: HierarchicalSelection | UnsupervisedSelection
): HierarchicalSelectionNode[] => {
    const result: HierarchicalSelectionNode[] = []

    const traverse = (node: HierarchicalSelectionNode) => {
        result.push(node)
        if (node.children) {
            node.children.forEach(traverse)
        }
    }

    if (root.children) {
        root.children.forEach(traverse)
    }

    return result
}

export const { reducer } = selectionFeature
